/* eslint-disable react/no-unknown-property */
'use client';
import { useEffect, useRef, useState, useMemo } from 'react';
import { Canvas, extend, useFrame } from '@react-three/fiber';
import { Environment, Lightformer } from '@react-three/drei';
import { BallCollider, CuboidCollider, Physics, RigidBody, useRopeJoint, useSphericalJoint } from '@react-three/rapier';
import { MeshLineGeometry, MeshLineMaterial } from 'meshline';

import * as THREE from 'three';
import { getLanyardContent } from '../lanyardDefaults';
import './Lanyard.css';

extend({ MeshLineGeometry, MeshLineMaterial });

function wrapText(ctx, text, maxWidth) {
  const lines = [];
  for (const paragraph of String(text || '').split(/\r?\n/)) {
    if (!paragraph) {
      lines.push('');
      continue;
    }
    let line = '';
    for (const character of paragraph) {
      if (line && ctx.measureText(line + character).width > maxWidth) {
        lines.push(line);
        line = character;
      } else {
        line += character;
      }
    }
    lines.push(line);
  }
  return lines;
}

function drawCardText(ctx, text, { fontSize, maxWidth, maxLines, lineHeight, centerY, align }) {
  ctx.fillStyle = '#333333';
  ctx.font = `bold ${fontSize}px Arial`;
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';
  const lines = wrapText(ctx, text, maxWidth).slice(0, maxLines);
  const firstY = centerY - ((lines.length - 1) * lineHeight) / 2;
  lines.forEach((line, index) => {
    ctx.fillText(line, align === 'center' ? 256 : 40, firstY + index * lineHeight);
  });
}

export default function Lanyard({ eventSource, position = [0, 0, 30], gravity = [0, -40, 0], fov = 20, transparent = true, content }) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const extraHeight = 280;
  const heightRatio = typeof window !== 'undefined'
    ? (window.innerHeight + extraHeight) / window.innerHeight
    : 1;
  const adjustedCameraPosition = [position[0], position[1], position[2] * heightRatio];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div
      className="lanyard-wrapper"
      style={{ '--lanyard-extra-height': `${extraHeight}px` }}
    >
      <Canvas
        camera={{ position: adjustedCameraPosition, fov: fov }}
        dpr={[1, isMobile ? 1.5 : 2]}
        gl={{ alpha: transparent }}
        eventSource={eventSource}
        onCreated={({ gl, setEvents }) => {
          gl.setClearColor(new THREE.Color(0x000000), transparent ? 0 : 1);
          setEvents({
            compute: (event, state) => {
              const bounds = gl.domElement.getBoundingClientRect();
              state.pointer.set(
                ((event.clientX - bounds.left) / bounds.width) * 2 - 1,
                -((event.clientY - bounds.top) / bounds.height) * 2 + 1
              );
              state.raycaster.setFromCamera(state.pointer, state.camera);
            },
          });
        }}
      >
        <ambientLight intensity={Math.PI} />
        <Physics gravity={gravity} timeStep={isMobile ? 1 / 30 : 1 / 60}>
          <Band key={content?.vertical ?? 0} isMobile={isMobile} eventSource={eventSource} content={content} />
        </Physics>
        <Environment blur={0.75}>
          <Lightformer
            intensity={2}
            color="white"
            position={[0, -1, 5]}
            rotation={[0, 0, Math.PI / 3]}
            scale={[100, 0.1, 1]}
          />
          <Lightformer
            intensity={3}
            color="white"
            position={[-1, -1, 1]}
            rotation={[0, 0, Math.PI / 3]}
            scale={[100, 0.1, 1]}
          />
          <Lightformer
            intensity={3}
            color="white"
            position={[1, 1, 1]}
            rotation={[0, 0, Math.PI / 3]}
            scale={[100, 0.1, 1]}
          />
          <Lightformer
            intensity={10}
            color="white"
            position={[-10, 0, 14]}
            rotation={[0, Math.PI / 2, Math.PI / 3]}
            scale={[100, 10, 1]}
          />
        </Environment>
      </Canvas>
    </div>
  );
}

function clampCardToHero(position, state, cardBody, eventSource) {
  const hero = eventSource?.current;
  if (!hero) return { position, changed: false };

  const heroBounds = hero.getBoundingClientRect();
  const bannerBounds = hero.firstElementChild.getBoundingClientRect();
  const canvasBounds = state.gl.domElement.getBoundingClientRect();
  if (!canvasBounds.width || !canvasBounds.height) return { position, changed: false };

  const rotation = cardBody.rotation();
  const visualOffset = new THREE.Vector3(0, -1.2, 0).applyQuaternion(
    new THREE.Quaternion(rotation.x, rotation.y, rotation.z, rotation.w)
  );
  const center = position.clone().add(visualOffset);
  const projected = center.clone().project(state.camera);
  const projectedRight = center.clone().add(new THREE.Vector3(1, 0, 0)).project(state.camera);
  const projectedUp = center.clone().add(new THREE.Vector3(0, 1, 0)).project(state.camera);
  const pixelsPerX = Math.abs(projectedRight.x - projected.x) * canvasBounds.width / 2;
  const pixelsPerY = Math.abs(projectedUp.y - projected.y) * canvasBounds.height / 2;
  if (!pixelsPerX || !pixelsPerY) return { position, changed: false };

  const centerX = canvasBounds.left + (projected.x + 1) * canvasBounds.width / 2;
  const centerY = canvasBounds.top + (1 - projected.y) * canvasBounds.height / 2;
  const radiusX = pixelsPerX * 3.3 + 12;
  const radiusY = pixelsPerY * 3.3 + 12;
  const minX = heroBounds.left + radiusX;
  const maxX = heroBounds.right - radiusX;
  const minY = bannerBounds.bottom + radiusY;
  const maxY = heroBounds.bottom - radiusY;
  const limitedX = Math.max(minX, Math.min(maxX, centerX));
  const limitedY = Math.max(minY, Math.min(maxY, centerY));
  const changed = Math.abs(limitedX - centerX) > 1 || Math.abs(limitedY - centerY) > 1;

  if (changed) {
    position.x += (limitedX - centerX) / pixelsPerX;
    position.y += (centerY - limitedY) / pixelsPerY;
  }

  return { position, changed };
}

function Band({ maxSpeed = 50, minSpeed = 0, isMobile = false, eventSource, content }) {
  const band = useRef(),
    fixed = useRef(),
    j1 = useRef(),
    j2 = useRef(),
    j3 = useRef(),
    card = useRef();
  const vec = new THREE.Vector3(),
    ang = new THREE.Vector3(),
    rot = new THREE.Vector3(),
    dir = new THREE.Vector3(),
    hookPosition = new THREE.Vector3(),
    hookRotation = new THREE.Quaternion();
  // 挂绳末端伸入半圆环，避免抗锯齿造成可见缝隙。
  const hookOffset = new THREE.Vector3(0, -1.2 + 2.25 * 1.3, -0.05);
  // 调整这些参数来改变反弹效果
  const segmentProps = { 
    type: 'dynamic', 
    canSleep: true, 
    colliders: false, 
    angularDamping: 2,      // 降低角阻尼，反弹更快（原来是4）
    linearDamping: 2        // 降低线性阻尼，反弹更快（原来是4）
  };
  
  const [curve] = useState(
    () =>
      new THREE.CatmullRomCurve3([new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()])
  );
  const [dragged, drag] = useState(false);
  const [hovered, hover] = useState(false);
  const { frontText, backText, icon, vertical } = getLanyardContent(content);
  const ropeLength = 1.6 + vertical / 200;

  const frontTexture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 720;
    return new THREE.CanvasTexture(canvas);
  }, []);

  useEffect(() => {
    const ctx = frontTexture.image.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 512, 720);
    drawCardText(ctx, frontText, {
      fontSize: 32,
      maxWidth: 420,
      maxLines: 4,
      lineHeight: 44,
      centerY: 450,
      align: 'center',
    });
    frontTexture.needsUpdate = true;

    if (!icon) return undefined;
    let isCancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      if (isCancelled) return;
      const scale = Math.min(200 / img.width, 200 / img.height);
      const width = img.width * scale;
      const height = img.height * scale;
      ctx.drawImage(img, 256 - width / 2, 200 - height / 2, width, height);
      frontTexture.needsUpdate = true;
    };
    img.src = icon;

    return () => {
      isCancelled = true;
    };
  }, [frontTexture, frontText, icon]);

  const backTexture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 720;
    return new THREE.CanvasTexture(canvas);
  }, []);

  useEffect(() => {
    const ctx = backTexture.image.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 512, 720);
    drawCardText(ctx, backText, {
      fontSize: 40,
      maxWidth: 432,
      maxLines: 8,
      lineHeight: 64,
      centerY: 360,
      align: 'left',
    });
    backTexture.needsUpdate = true;
  }, [backTexture, backText]);

  useEffect(() => () => {
    frontTexture.dispose();
    backTexture.dispose();
  }, [frontTexture, backTexture]);

  useRopeJoint(fixed, j1, [[0, 0, 0], [0, 0, 0], ropeLength]);
  useRopeJoint(j1, j2, [[0, 0, 0], [0, 0, 0], ropeLength]);
  useRopeJoint(j2, j3, [[0, 0, 0], [0, 0, 0], ropeLength]);
  useSphericalJoint(j3, card, [
    [0, 0, 0],
    [0, 1.5, 0]
  ]);

  useEffect(() => {
    if (hovered) {
      document.body.style.cursor = dragged ? 'grabbing' : 'grab';
      return () => void (document.body.style.cursor = 'auto');
    }
  }, [hovered, dragged]);

  const handlePointerUp = (e) => {
    e.target.releasePointerCapture(e.pointerId);
    drag(false);
  };

  const handlePointerDown = (e) => {
    e.target.setPointerCapture(e.pointerId);
    drag(new THREE.Vector3().copy(e.point).sub(vec.copy(card.current.translation())));
  };

  useFrame((state, delta) => {
    if (dragged) {
      vec.set(state.pointer.x, state.pointer.y, 0.5).unproject(state.camera);
      dir.copy(vec).sub(state.camera.position).normalize();
      vec.add(dir.multiplyScalar(state.camera.position.length()));
      [card, j1, j2, j3, fixed].forEach(ref => ref.current?.wakeUp());
      if (card.current) {
        const target = new THREE.Vector3(vec.x - dragged.x, vec.y - dragged.y, vec.z - dragged.z);
        const limited = clampCardToHero(target, state, card.current, eventSource);
        card.current.setNextKinematicTranslation(limited.position);
      }
    } else if (card.current) {
      const position = new THREE.Vector3().copy(card.current.translation());
      const limited = clampCardToHero(position, state, card.current, eventSource);
      if (limited.changed) {
        card.current.setTranslation(limited.position, true);
        card.current.setLinvel({ x: 0, y: 0, z: 0 }, true);
      }
    }
    if (fixed.current) {
      [j1, j2].forEach(ref => {
        if (!ref.current.lerped) ref.current.lerped = new THREE.Vector3().copy(ref.current.translation());
        const clampedDistance = Math.max(0.1, Math.min(1, ref.current.lerped.distanceTo(ref.current.translation())));
        ref.current.lerped.lerp(
          ref.current.translation(),
          delta * (minSpeed + clampedDistance * (maxSpeed - minSpeed))
        );
      });
      const cardRotation = card.current.rotation();
      hookRotation.set(cardRotation.x, cardRotation.y, cardRotation.z, cardRotation.w);
      hookPosition.copy(hookOffset).applyQuaternion(hookRotation).add(card.current.translation());
      curve.points[0].copy(hookPosition);
      curve.points[1].copy(j2.current.lerped);
      curve.points[2].copy(j1.current.lerped);
      curve.points[3].copy(fixed.current.translation());
      // 只调整中上段；末端必须贴合随卡片旋转的挂钩。
      curve.points[1].y *= 1.15;
      curve.points[2].y *= 1.15;
      band.current.geometry.setPoints(curve.getPoints(isMobile ? 16 : 32));
      ang.copy(card.current.angvel());
      rot.copy(card.current.rotation());
      card.current.setAngvel({ x: ang.x, y: ang.y - rot.y * 0.25, z: ang.z });
    }
  });

  curve.curveType = 'chordal';

  return (
    <>
      <group position={[0, 8.2, 0]}>
        <RigidBody ref={fixed} {...segmentProps} type="fixed" />
        <RigidBody position={[0.5, 0, 0]} ref={j1} {...segmentProps}>
          <BallCollider args={[0.1]} />
        </RigidBody>
        <RigidBody position={[1, 0, 0]} ref={j2} {...segmentProps}>
          <BallCollider args={[0.1]} />
        </RigidBody>
        <RigidBody position={[1.5, 0, 0]} ref={j3} {...segmentProps}>
          <BallCollider args={[0.1]} />
        </RigidBody>
        <RigidBody position={[2, 0, 0]} ref={card} {...segmentProps} type={dragged ? 'kinematicPosition' : 'dynamic'}>
          <CuboidCollider args={[0.8, 1.125, 0.01]} />
          <group
            scale={2.25}
            position={[0, -1.2, -0.05]}
            onPointerOver={() => hover(true)}
            onPointerOut={() => hover(false)}
            onPointerUp={handlePointerUp}
            onPointerDown={handlePointerDown}
          >
            <mesh
              position={[0, 0.1, 0.03]}
              onPointerOver={() => hover(true)}
              onPointerOut={() => hover(false)}
              onPointerUp={handlePointerUp}
              onPointerDown={handlePointerDown}
            >
              <planeGeometry args={[2.6, 3.6]} />
              <meshBasicMaterial transparent opacity={0} depthWrite={false} />
            </mesh>

            {/* 挂钩 - 半圆环设计 */}
            {/* 挂钩上面的半圆环 - 开口向下，更细 */}
            <mesh position={[0, 1.3, 0]} rotation={[0, 0, Math.PI]}>
              <torusGeometry args={[0.1, 0.025, 16, 16, Math.PI]} />
              <meshStandardMaterial color="#1a1a1a" metalness={0.9} roughness={0.1} />
            </mesh>
            
            {/* 半圆环的直径横线 - 2D矩形，上方圆角 */}
            <mesh position={[0, 1.3, 0]}>
              <shapeGeometry args={[
                (() => {
                  const shape = new THREE.Shape();
                  const width = 0.25;
                  const height = 0.05;
                  const radius = 0.015;
                  
                  shape.moveTo(-width/2 + radius, height/2);
                  shape.lineTo(width/2 - radius, height/2);
                  shape.quadraticCurveTo(width/2, height/2, width/2, height/2 - radius);
                  shape.lineTo(width/2, -height/2);
                  shape.lineTo(-width/2, -height/2);
                  shape.lineTo(-width/2, height/2 - radius);
                  shape.quadraticCurveTo(-width/2, height/2, -width/2 + radius, height/2);
                  
                  return shape;
                })()
              ]} />
              <meshStandardMaterial color="#1a1a1a" metalness={0.9} roughness={0.1} />
            </mesh>
            
            {/* 挂钩中间的竖杆 - 更长 */}
            <mesh position={[0, 1.15, 0]}>
              <cylinderGeometry args={[0.02, 0.02, 0.35, 16]} />
              <meshStandardMaterial color="#1a1a1a" metalness={0.9} roughness={0.1} />
            </mesh>
            
            {/* 卡片正面 - 有纹理 */}
            <mesh position={[0, 0, 0.011]}>
              <planeGeometry args={[1.6, 2.25]} />
              <meshPhysicalMaterial
                map={frontTexture}
                clearcoat={isMobile ? 0 : 1}
                clearcoatRoughness={0.15}
                roughness={0.9}
                metalness={0.8}
              />
            </mesh>
            
            {/* 卡片背面 - 显示学历信息 */}
            <mesh position={[0, 0, -0.011]} rotation={[0, Math.PI, 0]}>
              <planeGeometry args={[1.6, 2.25]} />
              <meshPhysicalMaterial
                map={backTexture}
                clearcoat={isMobile ? 0 : 1}
                clearcoatRoughness={0.15}
                roughness={0.9}
                metalness={0.8}
              />
            </mesh>
            
            {/* 卡片上的孔 - 2D圆形 */}
            <mesh position={[0, 1.0, 0.02]}>
              <circleGeometry args={[0.05, 32]} />
              <meshStandardMaterial color="#000000" metalness={0} roughness={1} />
            </mesh>
            {/* 卡片夹 - 2D圆形 */}
            <mesh position={[0, -0.2, 0]}>
              <circleGeometry args={[0.15, 32]} />
              <meshStandardMaterial color="#1a1a1a" roughness={0.3} />
            </mesh>
          </group>
        </RigidBody>
      </group>
      <mesh ref={band}>
        <meshLineGeometry />
        <meshLineMaterial
          color="white"
          depthTest={false}
          resolution={isMobile ? [1000, 2000] : [1000, 1000]}
          lineWidth={1}
        />
      </mesh>
    </>
  );
}
